import type { Middleware } from "openapi-fetch";
import { client as shellClient } from "./api/client.ts";
import type { SgWorkspace } from "./api/types.ts";
import { navigateTo, setWorkspaceHeaderMiddleware } from "@sg-protect/core";
import { type EventBusPayload, useEventBusListener } from "@sg-protect/federation-runtime";
import { type PropsWithChildren, useCallback, useEffect, useState } from "react";
import { SG_WORKSPACE_CHANGED_EVENT } from "./events.ts";
import { useWorkspaceContext } from "./WorkspaceContext.tsx";

export interface MiddlewareClient {
    use(...middleware: Middleware[]): void;
    eject(...middleware: Middleware[]): void;
}

// The shell’s own client is private, so it always rides along with whatever clients a module passes.
export function withShellClient(client: MiddlewareClient | MiddlewareClient[]): MiddlewareClient[] {
    return [shellClient, ...(Array.isArray(client) ? client : [client])];
}

export function useInstallWorkspaceHeader(client: MiddlewareClient | MiddlewareClient[]): boolean {
    const { currentWorkspace } = useWorkspaceContext();
    const [isMiddlewareInstalled, setIsMiddlewareInstalled] = useState(false);

    useEffect(() => {
        const clients = withShellClient(client);
        const middleware = setWorkspaceHeaderMiddleware({ workspaceId: currentWorkspace?.id });

        clients.forEach(c => c.use(middleware));
        setIsMiddlewareInstalled(true);

        return () => {
            clients.forEach(c => c.eject(middleware));
            setIsMiddlewareInstalled(false);
        };
    }, [currentWorkspace, client]);

    return isMiddlewareInstalled;
}

interface WorkspaceBoundaryProps extends PropsWithChildren {
    /** Returns the URL to full-page-reload to when the active workspace changes. */
    resolveDestination: (workspace: SgWorkspace) => string;
    /** API client(s) that should receive the workspace header middleware. Pass a memoized array when passing more than one. */
    client: MiddlewareClient | MiddlewareClient[];
}

export function WorkspaceBoundary({ children, resolveDestination, client }: WorkspaceBoundaryProps) {
    const isMiddlewareInstalled = useInstallWorkspaceHeader(client);

    const handleWorkspaceChange = useCallback(
        ({ workspace }: EventBusPayload<typeof SG_WORKSPACE_CHANGED_EVENT>) => {
            // Must be a full-page reload: React Query keys don't include the workspace id,
            // so a soft navigation would render stale data.
            navigateTo(resolveDestination(workspace));
        },
        [resolveDestination]
    );

    useEventBusListener(SG_WORKSPACE_CHANGED_EVENT, handleWorkspaceChange);

    if (!isMiddlewareInstalled) {
        return null;
    }

    return children;
}
